import type { NavigationMatch } from '@/domain/navigation';

import {
    CAGE_STATUS_ORDER,
    cageQuickSearchLabel,
    locationQuickSearchLabel,
    QUICK_SEARCH_GROUP_ORDER,
} from './quickSearchHelpers';
import type { CageStatus, QuickSearchItem } from './types';

type Translate = (key: string, options?: Record<string, string | number>) => string;

export function navigationMatchToQuickSearchItem(
    match: NavigationMatch,
    options: { isOptoScaleAdmin: boolean; isIdMatch?: boolean; t: Translate }
): QuickSearchItem | null {
    const { isOptoScaleAdmin, isIdMatch = true, t } = options;

    if (match.kind === 'cage') {
        if (
            match.clientId == null ||
            match.locationId == null ||
            match.cageId == null ||
            match.cageName == null
        ) {
            return null;
        }
        const locationName = match.locationName ?? '';
        const nameLabel = cageQuickSearchLabel(match.cageName, locationName);
        let label = nameLabel;
        if (isIdMatch) {
            label =
                isOptoScaleAdmin && match.clientName
                    ? t('quickSearch.idMatch.cageWithClient', {
                          name: match.cageName,
                          id: match.id,
                          location: locationName,
                          client: match.clientName,
                      })
                    : t('quickSearch.idMatch.cage', {
                          name: match.cageName,
                          id: match.id,
                          location: locationName,
                      });
        } else if (isOptoScaleAdmin && match.clientName) {
            label = locationQuickSearchLabel(nameLabel, match.clientName, true);
        }
        return {
            id: isIdMatch ? `id-cage-${match.id}` : `cage-${match.id}`,
            kind: 'cage',
            label,
            group: 'Cages',
            url: `/c/${match.clientId}/location/${match.locationId}/pen/${match.cageId}`,
            status: match.status,
            isIdMatch,
        };
    }

    if (match.kind === 'winch') {
        const bioscopeId = match.bioscopeId;
        if (bioscopeId == null) {
            return null;
        }
        if (
            match.clientId != null &&
            match.cageId != null &&
            match.locationId != null &&
            match.cageName != null
        ) {
            return {
                id: `id-winch-${match.id}-${bioscopeId}`,
                kind: 'winch',
                label: t('quickSearch.idMatch.winchPlaced', {
                    id: match.id,
                    cage: match.cageName,
                    location: match.locationName ?? '',
                }),
                group: 'Winches',
                url: `/c/${match.clientId}/location/${match.locationId}/pen/${match.cageId}/move-bioscope`,
                status: match.status,
                isIdMatch,
            };
        }
        if (!isOptoScaleAdmin) {
            return null;
        }
        return {
            id: `id-winch-${match.id}-${bioscopeId}`,
            kind: 'winch',
            label: t('quickSearch.idMatch.winchUnassigned', {
                id: match.id,
                bioscopeId,
            }),
            group: 'Winches',
            url: `/internal/bioscopes/${bioscopeId}`,
            isIdMatch,
        };
    }

    if (match.kind !== 'bioscope') {
        return null;
    }

    if (
        match.clientId != null &&
        match.cageId != null &&
        match.locationId != null &&
        match.cageName != null
    ) {
        return {
            id: isIdMatch ? `id-bioscope-${match.id}` : `bioscope-${match.id}`,
            kind: 'bioscope',
            label: t('quickSearch.idMatch.bioscopePlaced', {
                id: match.id,
                cage: match.cageName,
                location: match.locationName ?? '',
            }),
            group: 'Bioscopes',
            url: `/c/${match.clientId}/location/${match.locationId}/pen/${match.cageId}`,
            status: match.status,
            isIdMatch,
        };
    }

    if (!isOptoScaleAdmin) {
        return null;
    }

    return {
        id: isIdMatch ? `id-bioscope-${match.id}` : `bioscope-${match.id}`,
        kind: 'bioscope',
        label: t('quickSearch.idMatch.bioscopeUnassigned', { id: match.id }),
        group: 'Bioscopes',
        url: `/internal/bioscopes/${match.id}`,
        isIdMatch,
    };
}

function itemIdentityKey(item: QuickSearchItem): string {
    if (item.kind === 'cage') {
        const match = item.id.match(/(?:^|-)cage-(\d+)$/);
        if (match) {
            return `cage:${match[1]}`;
        }
    }
    if (item.kind === 'bioscope') {
        const match = item.id.match(/(?:^|-)bioscope-(\d+)$/);
        if (match) {
            return `bioscope:${match[1]}`;
        }
    }
    if (item.kind === 'winch') {
        const match = item.id.match(/(?:^|-)winch-(\d+)-(\d+)$/);
        if (match) {
            return `winch:${match[1]}:${match[2]}`;
        }
    }
    return item.id;
}

export function mergeQuickSearchItems(
    idMatches: QuickSearchItem[],
    nameMatches: QuickSearchItem[]
): QuickSearchItem[] {
    const seen = new Set<string>();
    const merged: QuickSearchItem[] = [];
    for (const item of [...idMatches, ...nameMatches]) {
        const key = itemIdentityKey(item);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        merged.push(item);
    }
    return merged;
}

export function flattenInDisplayOrder(items: QuickSearchItem[]): QuickSearchItem[] {
    const groups = new Map<string, QuickSearchItem[]>();
    const order: string[] = [];
    for (const item of items) {
        const existing = groups.get(item.group);
        if (existing == null) {
            groups.set(item.group, [item]);
            order.push(item.group);
            continue;
        }
        existing.push(item);
    }
    return order.flatMap((group) => groups.get(group) ?? []);
}

function groupRank(group: string): number {
    const index = (QUICK_SEARCH_GROUP_ORDER as readonly string[]).indexOf(group);
    return index === -1 ? 99 : index;
}

function statusRank(status: CageStatus | undefined): number {
    if (status == null) {
        return CAGE_STATUS_ORDER.length;
    }
    const index = CAGE_STATUS_ORDER.indexOf(status);
    return index === -1 ? CAGE_STATUS_ORDER.length : index;
}

export function matchesQuickSearchQuery(label: string, query: string): boolean {
    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
        return true;
    }
    const haystack = label.toLowerCase();
    return tokens.every((token) => haystack.includes(token));
}

function itemSearchText(item: QuickSearchItem): string {
    return item.parentLabel ? `${item.parentLabel} ${item.label}` : item.label;
}

export function filterAndSortQuickSearchItems(
    items: QuickSearchItem[],
    search: string,
    idMatches: QuickSearchItem[] = []
): QuickSearchItem[] {
    const term = search.toLowerCase().trim();
    const nameMatches = term
        ? items.filter((item) => matchesQuickSearchQuery(itemSearchText(item), term))
        : items;
    const merged = mergeQuickSearchItems(idMatches, nameMatches);

    const sorted = merged.sort((a, b) => {
        const aId = a.isIdMatch ? 0 : 1;
        const bId = b.isIdMatch ? 0 : 1;
        if (aId !== bId) {
            return aId - bId;
        }

        const aGroup = groupRank(a.group);
        const bGroup = groupRank(b.group);
        if (aGroup !== bGroup) {
            return aGroup - bGroup;
        }

        const aStatus = statusRank(a.status);
        const bStatus = statusRank(b.status);
        if (aStatus !== bStatus) {
            return aStatus - bStatus;
        }

        if (term) {
            const aStarts = a.label.toLowerCase().startsWith(term);
            const bStarts = b.label.toLowerCase().startsWith(term);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
        }

        if (a.kind === 'page' && b.kind === 'page') {
            return (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0);
        }

        return a.label.localeCompare(b.label);
    });

    return flattenInDisplayOrder(sorted);
}
